import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import kycService from "../../../services/api/kyc.service";
import { authService } from "@/services/api/auth.service";

const BasicInfoForm = ({ onComplete }) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    mobileNo: "",
    fullName: "",
    panNumber: "",
    dateOfBirth: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    nationality: "Indian",
  });

  const [kycStatus, setKycStatus] = useState(null); // null = loading

  const [originalMobileNo, setOriginalMobileNo] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const profileData = await kycService.getProfile();
        const savedFormData = kycService.getSavedFormData(1);

        setFormData((prev) => ({
          ...prev,
          email: savedFormData?.email || profileData.email || "",
          mobileNo: savedFormData?.mobileNo || profileData.mobileNo || "",
          fullName: savedFormData?.fullName || profileData.fullName || "",
          panNumber:
            savedFormData?.panNumber ||
            profileData?.kycDetails?.panNumber ||
            "",
          // NEW: Date of Birth handling
          // Replace the existing dateOfBirth handling in the useEffect with this:
          dateOfBirth:
            savedFormData?.dateOfBirth ||
            (profileData?.kycDetails?.dateOfBirth
              ? new Date(profileData.kycDetails.dateOfBirth).toISOString().split('T')[0]
              : ""),
          nationality:
            savedFormData?.nationality ||
            profileData?.kycDetails?.nationality ||
            "Indian",
          line1:
            savedFormData?.address?.line1 ||
            profileData?.kycDetails?.address?.line1 ||
            "",
          line2:
            savedFormData?.address?.line2 ||
            profileData?.kycDetails?.address?.line2 ||
            "",
          city:
            savedFormData?.address?.city ||
            profileData?.kycDetails?.address?.city ||
            "",
          state:
            savedFormData?.address?.state ||
            profileData?.kycDetails?.address?.state ||
            "",
          pincode:
            savedFormData?.address?.pincode ||
            profileData?.kycDetails?.address?.pincode ||
            "",
          country:
            savedFormData?.address?.country ||
            profileData?.kycDetails?.address?.country ||
            "",
        }));

        setOriginalMobileNo(profileData.mobileNo || "");

        // Set KYC status for lock check
        const status = profileData?.kycDetails?.verificationStatus || null;
        setKycStatus(status);
      } catch (error) {
        console.error("Failed to load user data:", error);
        toast.error("Failed to load user data");
      }
    };

    loadExistingData();
  }, []);

  useEffect(() => {
    let timer;
    if (resendDisabled && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setResendDisabled(false);
      setCountdown(30);
    }
    return () => clearInterval(timer);
  }, [resendDisabled, countdown]);

  // Fetch state and country from pincode using free API
  const fetchPincodeData = async (pincode) => {
    if (pincode.length !== 6) return;
    setPincodeLoading(true);
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();
      if (data && data[0] && data[0].Status === "Success" && data[0].PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        const state = postOffice.State || "";
        const city = postOffice.District || postOffice.Block || postOffice.Division || postOffice.Region || "";
        const country = postOffice.Country || "India";
        setFormData((prev) => ({ ...prev, state, city, country }));
        setErrors((prev) => ({ ...prev, state: "", city: "", country: "" }));
      } else {
        toast.error("Invalid pincode or no data found.");
      }
    } catch (err) {
      console.error("Pincode API error:", err);
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    if (id === "mobileNo") {
      // Allow only digits, max 10
      if (!/^\d{0,10}$/.test(value)) return;

      // Reset mobile verification status when number changes
      if (value !== originalMobileNo) {
        setMobileVerified(false);
        setShowOtpVerification(false);
        setOtpSent(false);
        setOtp("");
      }
    }

    if (id === "panNumber") {
      // Allow only alphanumeric, max 10, auto-uppercase
      const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
      if (cleaned.length > 10) return;
      setFormData((prev) => ({ ...prev, panNumber: cleaned }));
      validateField("panNumber", cleaned);
      return;
    }

    if (id === "pincode") {
      // Allow only digits, max 6
      if (!/^\d{0,6}$/.test(value)) return;
      setFormData((prev) => ({ ...prev, pincode: value }));
      validateField("pincode", value);
      if (value.length === 6) {
        fetchPincodeData(value);
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
    validateField(id, value);
  };

  const validateField = (id, value) => {
    let error = "";

    switch (id) {
      case "fullName":
        if (!value.trim()) {
          error = "Full name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          error = "Full name must contain only letters and spaces";
        }
        break;
      case "mobileNo":
        if (!value.trim()) {
          error = "Mobile number is required";
        } else if (!/^[0-9]{10}$/.test(value)) {
          error = "Enter a valid 10-digit mobile number";
        }
        break;
      case "email":
        if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value))
          error = "Enter a valid email address";
        break;
      case "panNumber":
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value))
          error = "Enter a valid PAN number";
        break;
      case "dateOfBirth": // NEW VALIDATION
        if (!value.trim()) {
          error = "Date of birth is required";
        } else {
          const dob = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < dob.getDate())
          ) {
            age--;
          }

          if (age < 18) {
            error = "You must be at least 18 years old";
          } else if (age > 100) {
            error = "Please enter a valid date of birth";
          }
        }
        break;
      case "line1":
        if (!value.trim()) error = "Address Line 1 is required";
        break;
      case "city":
        if (!value.trim()) error = "City is required";
        break;
      case "state":
        if (!value.trim()) error = "State is required";
        break;
      case "pincode":
        if (!/^[0-9]{6}$/.test(value)) error = "Enter a valid 6-digit pincode";
        break;
      case "country":
        if (!value.trim()) error = "Country is required";
        break;
      case "nationality":
        if (!value.trim()) error = "Nationality is required";
        break;
      case "otp":
        if (!value.trim()) {
          error = "OTP is required";
        } else if (!/^[0-9]{6}$/.test(value)) {
          error = "Enter a valid 6-digit OTP";
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [id]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    const requiredFields = formFields.filter((field) => field.id !== "line2");

    requiredFields.forEach(({ id }) => {
      const value = formData[id];
      const error = validateField(id, value);
      if (error) {
        newErrors[id] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if mobile number exists before proceeding
  const checkMobileExists = async (mobileNo) => {
    try {
      // You might need to create this API endpoint or use existing one
      // This is a placeholder - replace with your actual API call
      const response = await authService.checkMobileExists(mobileNo);
      return response.exists;
    } catch (error) {
      // If API doesn't exist, we'll handle it in the main submission
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields first
    if (!validateAllFields()) {
      return;
    }

    // Check if mobile number has changed and needs verification
    const mobileChanged = formData.mobileNo !== originalMobileNo;

    if (mobileChanged && !mobileVerified) {
      // Check if mobile already exists before sending OTP
      try {
        setLoading(true);

        // First try to send OTP - if mobile exists, backend will return error
        const response = await authService.sendMobileOtp(formData.mobileNo);

        // Check for success: false even on 200 OK
        if (response && response.success === false) {
          throw response;
        }

        // If we reach here, mobile doesn't exist, proceed with OTP verification
        setShowOtpVerification(true);
        setOtpSent(true);
        setResendDisabled(true);
        setCountdown(30);
        toast.success("OTP sent successfully");
        return;
      } catch (error) {
        console.error("OTP send error:", error);

        // Support both raw backend object and Error instance
        const apiMessage = error.message || error.response?.data?.message || "Failed to send OTP";

        // Handle mobile already exists error
        if (
          apiMessage.toLowerCase().includes("mobile") ||
          apiMessage.toLowerCase().includes("phone") ||
          apiMessage.toLowerCase().includes("exists") ||
          error.response?.status === 409
        ) {
          const friendlyMessage = "Mobile number already exists";
          setErrors((prev) => ({
            ...prev,
            mobileNo: friendlyMessage,
          }));
          toast.error(friendlyMessage);
        } else {
          // Other errors while sending OTP
          toast.error(apiMessage);
        }
        return;
      } finally {
        setLoading(false);
      }
    }

    // Proceed with form submission
    await submitForm();
  };

  const submitForm = async () => {
    setLoading(true);

    const formattedData = {
      email: formData.email,
      mobileNo: formData.mobileNo,
      fullName: formData.fullName,
      panNumber: formData.panNumber,
      dateOfBirth: formData.dateOfBirth, // NEW FIELD
      nationality: formData.nationality,
      address: {
        line1: formData.line1,
        line2: formData.line2 || "",
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        country: formData.country,
      },
      // NEW: KYC Flow Details to match backend structure
      kyc: {
        idDetails: {
          documentType: "PAN",
          flowType: "NUMBER",
          metadata: {
            kycItemData: {
              panNumber: formData.panNumber,
              fullName: formData.fullName,
              dateOfBirth: formData.dateOfBirth,
            },
          },
        },
      },
    };

    try {
      const response = await kycService.processStep(1, formattedData);

      // Check for success: false even on 200 OK
      if (response && response.success === false) {
        throw response;
      }

      toast.success("Basic info updated successfully");

      if (typeof onComplete === "function") {
        onComplete();
      } else {
        navigate("/kyc");
      }
    } catch (error) {
      console.error("Form submission error full dump:", error);

      // Support both raw backend object and Axios error
      const apiMessage =
        error.message || error.response?.data?.message || "Failed to update basic info";

      if (error.field === "panNumber") {
        setErrors((prev) => ({ ...prev, panNumber: apiMessage }));
        toast.error(apiMessage);
      } else if (apiMessage?.toLowerCase().includes("email")) {
        setErrors((prev) => ({ ...prev, email: apiMessage }));
        toast.error(apiMessage);
      } else if (
        apiMessage?.toLowerCase().includes("mobile") ||
        apiMessage?.toLowerCase().includes("phone")
      ) {
        setErrors((prev) => ({ ...prev, mobileNo: apiMessage }));
        toast.error(apiMessage);
      } else if (apiMessage) {
        toast.error(apiMessage);
      } else {
        toast.error("Failed to update basic info");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendDisabled) return;

    setLoading(true);
    try {
      await authService.sendMobileOtp(formData.mobileNo);
      setResendDisabled(true);
      setCountdown(30);
      toast.success("OTP resent successfully");
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    const otpError = validateField("otp", otp);
    if (otpError) return;

    setLoading(true);

    try {
      const response = await authService.singleverifyMobile({
        mobileNo: formData.mobileNo,
        otp,
      });

      // Check for success: false even on 200 OK
      if (response && response.success === false) {
        throw response;
      }

      toast.success("Mobile number verified successfully");
      setMobileVerified(true);
      setShowOtpVerification(false);
      setOriginalMobileNo(formData.mobileNo);

      // Proceed with form submission after verification
      await submitForm();
    } catch (error) {
      const errorMsg = error.message || error.response?.data?.message || "Failed to verify OTP";
      toast.error(errorMsg);
      setErrors((prev) => ({ ...prev, otp: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const formFields = [
    {
      id: "fullName",
      label: "Full Name (As on PAN Card)",
      type: "text",
      placeholder: "E.g. Raj Kumar Sharma",
    },
    {
      id: "mobileNo",
      label: "Mobile Number",
      type: "tel",
      placeholder: "E.g. 9876543210",
    },
    {
      id: "email",
      label: "Email",
      type: "email",
      placeholder: "E.g. raj.sharma@example.com",
    },
    {
      id: "panNumber",
      label: "PAN Number",
      type: "text",
      placeholder: "E.g. ABCDE1234F",
    },
    {
      id: "dateOfBirth", // NEW FIELD
      label: "Date of Birth",
      type: "date",
      placeholder: "",
    },
    {
      id: "nationality",
      label: "Nationality",
      type: "text",
      placeholder: "E.g. Indian",
    },
    {
      id: "line1",
      label: "Address Line 1",
      type: "text",
      placeholder: "E.g. 221B Baker Street",
    },
    {
      id: "line2",
      label: "Address Line 2",
      type: "text",
      placeholder: "E.g. Near City Mall (Optional)",
    },
    { id: "city", label: "City", type: "text", placeholder: "E.g. Mumbai" },
    {
      id: "state",
      label: "State",
      type: "text",
      placeholder: "Auto-filled from Pincode",
    },
    {
      id: "pincode",
      label: "Pincode",
      type: "text",
      placeholder: "E.g. 400001",
      maxLength: 6,
    },
    {
      id: "country",
      label: "Country",
      type: "text",
      placeholder: "Auto-filled from Pincode",
    },
  ];

  // KYC Lock logic
  const LOCKED_STATUSES = ["pending", "verified", "in-review"];
  const isLocked = LOCKED_STATUSES.includes(kycStatus);

  const getBannerData = () => {
    switch (kycStatus) {
      case "pending":
        return {
          bg: "bg-blue-50 border-blue-200",
          icon: "⏳",
          text: "Verification in Progress",
          sub: "Your details are being reviewed. Editing is disabled until verification is complete."
        };
      case "in-review":
        return {
          bg: "bg-amber-50 border-amber-200",
          icon: "📝",
          text: "Under Manual Review",
          sub: "An admin is currently reviewing your documents."
        };
      case "verified":
        return {
          bg: "bg-green-50 border-green-200",
          icon: "✅",
          text: "KYC Verified",
          sub: "Your profile is verified. Details cannot be edited for security reasons."
        };
      default:
        return null;
    }
  };

  const banner = getBannerData();

  return (
    <div className="flex justify-center px-4 sm:px-6 lg:px-8 mt-15">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-lg p-6 sm:p-8 relative">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-600 mb-4 sm:mb-0 text-center sm:text-left">
            Your Basic Profile
          </h2>
        </div>

        {/* KYC Lock Banner */}
        {isLocked && banner && (
          <div className={`mb-6 flex items-start gap-3 border rounded-xl p-4 ${banner.bg}`}>
            <span className="text-2xl">{banner.icon}</span>
            <div>
              <p className="font-semibold text-gray-800">{banner.text}</p>
              <p className="text-sm text-gray-600 mt-0.5">{banner.sub}</p>
            </div>
          </div>
        )}

        {/* Read-only locked view */}
        {isLocked ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {formFields.map(({ id, label }) => (
              <div key={id}>
                <label className="block text-gray-500 text-sm font-medium mb-1">{label}</label>
                <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                  {formData[id] || <span className="text-gray-400 italic">Not provided</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Editable form — shown only when status is draft / rejected */
          <form onSubmit={handleSubmit}>
            {showOtpVerification ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-blue-700 mb-4">
                  Verify Your Mobile Number
                </h3>
                <p className="text-gray-700 mb-4">
                  We've sent an OTP to verify your new mobile number:{" "}
                  {formData.mobileNo}
                </p>

                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">
                    Enter OTP *
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => {
                      if (/^\d{0,6}$/.test(e.target.value)) {
                        setOtp(e.target.value);
                        validateField("otp", e.target.value);
                      }
                    }}
                    placeholder="Enter 6-digit OTP"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${errors.otp
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                      }`}
                  />
                  {errors.otp && (
                    <p className="text-sm text-red-600 mt-1">{errors.otp}</p>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={loading}
                    className={`bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200 ${loading ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>

                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendDisabled || loading}
                    className={`text-blue-600 underline ${resendDisabled || loading
                      ? "opacity-60 cursor-not-allowed"
                      : "hover:text-blue-800"
                      }`}
                  >
                    {resendDisabled ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowOtpVerification(false);
                      setOtpSent(false);
                      setOtp("");
                      setErrors((prev) => ({ ...prev, otp: "" }));
                    }}
                    className="text-gray-600 underline hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {formFields.map(({ id, label, type, placeholder, maxLength }) => {
                  const isAutoFilled =
                    (id === "state" || id === "country" || id === "city") &&
                    formData.pincode.length === 6 &&
                    formData[id];
                  const isMobile = id === "mobileNo";
                  const isPan = id === "panNumber";
                  const isPincode = id === "pincode";
                  return (
                    <div key={id}>
                      <label
                        htmlFor={id}
                        className="block text-gray-700 font-medium mb-1"
                      >
                        {label} {id !== "line2" && "*"}
                        {isAutoFilled && (
                          <span className="ml-2 text-xs text-green-600 font-normal">
                            (Auto-filled)
                          </span>
                        )}
                        {isPincode && pincodeLoading && (
                          <span className="ml-2 text-xs text-blue-500 font-normal">
                            Fetching...
                          </span>
                        )}
                      </label>
                      <input
                        id={id}
                        type={type}
                        value={formData[id]}
                        onChange={handleChange}
                        placeholder={placeholder}
                        required={id !== "line2"}
                        maxLength={
                          isPan ? 10 : isMobile ? 10 : isPincode ? 6 : maxLength || undefined
                        }
                        inputMode={isMobile || isPincode ? "numeric" : undefined}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:outline-none ${errors[id]
                          ? "border-red-500 focus:ring-red-500"
                          : isAutoFilled
                            ? "border-green-400 bg-green-50 focus:ring-green-300 text-gray-700"
                            : "border-gray-300 focus:ring-blue-500"
                          }`}
                      />
                      {errors[id] && (
                        <p className="text-sm text-red-600 mt-1">{errors[id]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-center mt-8">
              <button
                type="submit"
                disabled={loading}
                className={`bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200 ${loading ? "opacity-60 cursor-not-allowed" : ""
                  }`}
              >
                {loading ? "Processing..." : "Upload Details"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BasicInfoForm;
